import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { UserCog, LogOut } from 'lucide-react';
import { useState } from 'react';

/**
 * Persistent banner shown when a super admin is impersonating a tenant.
 * Sits above every dashboard page. Clicking "Exit" restores the super admin's
 * original session from the local token stack.
 */
export function ImpersonationBanner() {
  const { user, isImpersonating, stopImpersonating } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!isImpersonating) return null;

  const exit = async () => {
    setBusy(true);
    try {
      await stopImpersonating();
      toast.success('Returned to your super admin session');
    } catch {
      toast.error('Failed to exit impersonation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/[0.15] to-slate-900/40 p-3 mb-4 flex items-center justify-between gap-4 flex-wrap"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
          <UserCog className="w-5 h-5 text-amber-300" />
        </div>
        <div className="text-sm">
          <div className="text-white font-medium">
            Impersonating <span className="font-mono text-amber-300">{user?.email}</span>
          </div>
          <div className="text-xs text-slate-400">
            Signed in via super admin session ({user?.impersonator_email}). Every action is logged.
          </div>
        </div>
      </div>
      <Button
        onClick={exit}
        disabled={busy}
        variant="outline"
        className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
        data-testid="exit-impersonation-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Exit impersonation
      </Button>
    </div>
  );
}
